import { getDataset } from "./agent-dataset-cache";
import type { AgentChartInput } from "./validate-chart-spec";

function msToIsoDate(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

function inferDatasetDateRange(
  ds: NonNullable<ReturnType<typeof getDataset>>,
): { from?: string; to?: string; key?: string } | undefined {
  const rows = ds.rows;
  if (!rows.length) return undefined;

  if (ds.columns.includes("day")) {
    const days = rows
      .map((r) => r.day)
      .filter((d): d is string => typeof d === "string" && d.length > 0)
      .sort();
    if (days.length) return { key: "day", from: days[0], to: days[days.length - 1] };
  }

  if (ds.columns.includes("timestampMs")) {
    const ts = rows
      .map((r) => Number(r.timestampMs))
      .filter((n) => Number.isFinite(n))
      .sort((a, b) => a - b);
    if (ts.length) {
      return {
        key: "timestampMs",
        from: msToIsoDate(ts[0]!),
        to: msToIsoDate(ts[ts.length - 1]!),
      };
    }
  }

  return undefined;
}

/** Tool output returned to the model after a successful agent_chart call. */
export function buildAgentChartToolResult(spec: AgentChartInput): Record<string, unknown> {
  const ds = getDataset(spec.datasetId);
  const dataRange = ds ? inferDatasetDateRange(ds) : undefined;

  return {
    ok: true,
    renderedIn: "agent_sidebar_chart_bubble",
    displayNote:
      "The interactive chart appears in the Vybe Agent sidebar for the user. There is no image URL — do not use markdown image syntax (![]()). Briefly describe what was charted.",
    chartType: spec.chartType,
    title: spec.title,
    datasetId: spec.datasetId,
    rowCount: ds?.rows.length ?? 0,
    seriesKeys: spec.seriesKeys,
    xKey: spec.xKey,
    dataRange,
  };
}
